import { useEffect, useState } from "react";
import { AlertTriangle, X, Loader2, Wand2, Check, ShieldCheck } from "lucide-react";
import { apiClient } from "../../lib/api";
import { useDesignModel, modelLabel } from "../../lib/useDesignModel";

type Seo = { title: string; description: string; tags: string[] };
type Warning = { category: "technique" | "digital" | "text"; term?: string; message: string; severity: "high" | "medium" | "low" };
type CheckResult = {
  visibleText: string; technique: string; isEmbroideryLook: boolean; subjects: string[];
  inspirationIsDigital: boolean; warnings: Warning[];
};

const PRODUCT_TYPES = ["t-shirt", "sweatshirt", "hoodie", "long sleeve shirt", "tank top"];
const SEVERITY_STYLE: Record<string, string> = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-gray-50 text-gray-600 border-gray-200",
};
const CAT_LABEL: Record<string, string> = { technique: "Teknik iddiası", digital: "Dijital/ürün uyumsuzluğu", text: "Metin farkı" };

// Tasarim gerceği ile baslik/aciklama/tag tutarliligini denetler; uyumsuzlukta
// ilham listesinden de destek alarak yeniden SEO uretir. Aktarim oncesi calisir.
export function DesignConsistencyModal({
  imageUrl, seo, targetProductType, niche, analysisId, templateId, onApply, onProceed, onClose,
}: {
  imageUrl: string;
  seo: Seo;
  targetProductType: string;
  niche?: string;
  analysisId?: string | null;
  /** Secili liste sablonu: urunun marka/modeli yeni SEO'ya girer */
  templateId?: string;
  onApply: (seo: Seo) => void;   // yeni SEO ile devam
  onProceed: () => void;         // mevcut SEO ile devam
  onClose: () => void;           // iptal
}) {
  const { models, model, setModel } = useDesignModel("erc:model:design-consistency");
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inspiration, setInspiration] = useState<{ title?: string; description?: string; tags?: string[] }>({});
  const [targetType, setTargetType] = useState(targetProductType || "t-shirt");
  const [regen, setRegen] = useState<Seo | null>(null);
  const [regenerating, setRegenerating] = useState(false);

  // İlham listesi + denetim
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      let insp: { title?: string; description?: string; tags?: string[]; rawTitle?: string; rawTags?: string[] } = {};
      if (analysisId) {
        try {
          const a = await apiClient.getAnalysis(analysisId);
          const src = (a as unknown as { source?: { title?: string; tags?: string[] } }).source;
          insp = {
            title: a.suggestedTitle || src?.title || "",
            description: a.suggestedDescription || "",
            tags: (a.suggestedTags && a.suggestedTags.length ? a.suggestedTags : src?.tags) || [],
            rawTitle: src?.title || "",
            rawTags: src?.tags || [],
          };
        } catch { /* ilham yoksa devam */ }
      }
      if (cancelled) return;
      setInspiration({ title: insp.title, description: insp.description, tags: insp.tags });
      try {
        const r = await apiClient.checkDesignConsistency({
          imageUrl, title: seo.title, description: seo.description, tags: seo.tags,
          targetProductType: targetType,
          inspirationTitle: insp.rawTitle || insp.title, inspirationTags: insp.rawTags || insp.tags,
        });
        if (!cancelled) setResult(r);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Denetim başarısız");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleRegenerate() {
    if (!result) return;
    setRegenerating(true);
    try {
      const avoidTerms = result.warnings.map((w) => w.term).filter(Boolean) as string[];
      const out = await apiClient.generateSeoFromDesign({
        design: { visibleText: result.visibleText, subjects: result.subjects, technique: result.technique },
        inspiration,
        targetProductType: targetType,
        niche,
        avoidTerms,
        inspirationIsDigital: result.inspirationIsDigital,
        templateId,
      });
      setRegen({ title: out.title, description: out.description, tags: out.tags });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Yeniden üretilemedi");
    }
    setRegenerating(false);
  }

  const warnings = result?.warnings ?? [];
  const hasWarnings = warnings.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b p-4">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            {hasWarnings ? <AlertTriangle className="h-5 w-5 text-amber-500" /> : <ShieldCheck className="h-5 w-5 text-emerald-500" />}
            Tasarım–Başlık Tutarlılık Denetimi
          </h3>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div className="flex items-start gap-3">
            <img src={imageUrl} alt="" className="h-24 w-24 flex-shrink-0 rounded-lg border border-gray-200 object-contain bg-gray-50" />
            <div className="min-w-0 flex-1 text-xs text-gray-600">
              <p className="line-clamp-2"><b>Başlık:</b> {seo.title || "—"}</p>
              {result && (
                <>
                  <p className="mt-1"><b>Tasarımdaki yazı:</b> {result.visibleText || "(yazı yok)"}</p>
                  <p className="mt-0.5"><b>Teknik:</b> {result.technique || "—"}{result.isEmbroideryLook ? " · nakış görünümü" : ""}</p>
                  {result.inspirationIsDigital && <p className="mt-0.5 text-amber-600">İlham bir PNG/dijital listesi — dijital kelimeler ayıklanacak.</p>}
                </>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-gray-400"><Loader2 className="h-4 w-4 animate-spin" /> Tasarım denetleniyor…</div>
          ) : error ? (
            <p className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{error}</p>
          ) : !hasWarnings ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
              ✓ Başlık/açıklama/etiketler tasarımla tutarlı görünüyor. Devam edebilirsin.
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-600">{warnings.length} uyarı bulundu:</p>
              {warnings.map((w, i) => (
                <div key={i} className={`rounded-lg border px-3 py-2 text-xs ${SEVERITY_STYLE[w.severity]}`}>
                  <span className="font-semibold">{CAT_LABEL[w.category]}{w.term ? ` · "${w.term}"` : ""}:</span> {w.message}
                </div>
              ))}

              {/* Yeniden üretim */}
              <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-indigo-700">Yeniden üret →</span>
                  <label className="text-[11px] text-gray-600">Hedef ürün:</label>
                  <select value={targetType} onChange={(e) => setTargetType(e.target.value)}
                    className="rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    {PRODUCT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <select value={model} onChange={(e) => setModel(e.target.value)}
                    className="rounded border border-gray-200 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200">
                    {models.map((m) => <option key={m.id} value={m.id}>{modelLabel(m)}</option>)}
                  </select>
                  <button onClick={handleRegenerate} disabled={regenerating}
                    className="ml-auto flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                    {regenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Başlık/Açıklama/Tag Üret
                  </button>
                </div>

                {regen && (
                  <div className="mt-3 space-y-2 rounded-lg border border-gray-200 bg-white p-2.5">
                    <div>
                      <span className="text-[10px] font-medium text-gray-500 uppercase">Yeni başlık</span>
                      <p className="text-xs text-gray-800">{regen.title}</p>
                    </div>
                    <div>
                      <span className="text-[10px] font-medium text-gray-500 uppercase">Açıklama</span>
                      <p className="line-clamp-3 text-[11px] text-gray-600">{regen.description}</p>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {regen.tags.map((t, i) => <span key={i} className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-700 border border-emerald-200">{t}</span>)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t p-3">
          <button onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">İptal</button>
          {regen ? (
            <button onClick={() => onApply(regen)} className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700">
              <Check className="h-4 w-4" /> Uygula ve Aktar
            </button>
          ) : (
            <button onClick={onProceed} disabled={loading}
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${hasWarnings ? "bg-gray-600 hover:bg-gray-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
              {hasWarnings ? "Yine de Aktar" : "Aktar"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
